// src/pages/promos/ViewPromos.tsx
import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Modal from "@/components/shared/ui/Modal";
import ConfirmDialog from "@/components/shared/ui/ConfirmDialog";
import PlaneLoadingScreen from "@/components/shared/ui/PageLoadingScreen";

import PromosTable, { type PromosTableFilters } from "@/components/promos/PromosTable";
import TablePagination from "@/components/shared/ui/TablePagination";
import SimulatorModal from "@/components/promos/SimulatorModal";
import CodeBatchModal from "@/components/promos/CodeBatchModal";

import {
  usePromosList,
  usePromoAnalytics,
  useDeletePromoMutation,
  useSetPromoStatusMutation,
} from "@/api/features/promos";
import type { Promo } from "@/api/features/promos";

function AnalyticsModal({ promo, onClose }: { promo: Promo; onClose: () => void }) {
  const { data, isLoading } = usePromoAnalytics(promo.id);
  const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
    <div className="flex justify-between border-b border-gray-100 py-2 text-sm last:border-0">
      <span className="text-gray-500">{k}</span>
      <span className="font-medium text-gray-900">{v}</span>
    </div>
  );
  const fmt = (n: number | null | undefined) =>
    n == null ? "∞" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return (
    <Modal open title={`Analytics — ${promo.name}`} onClose={onClose}>
      {isLoading || !data ? (
        <div className="py-8 text-center text-gray-400">Loading…</div>
      ) : (
        <div>
          <Row k="Confirmed redemptions" v={data.confirmedCount} />
          <Row k="Reserved (in-flight)" v={data.reservedCount} />
          <Row k="Redemptions remaining" v={fmt(data.redemptionsRemaining)} />
          <Row k="Discount given" v={fmt(data.discountGiven)} />
          <Row k="Discount reserved" v={fmt(data.discountReserved)} />
          <Row k="Budget remaining" v={fmt(data.budgetRemaining)} />
          {data.redemptionsByStatus?.length ? (
            <div className="mt-3">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">By status</div>
              {data.redemptionsByStatus.map((r) => (
                <Row key={r.status} k={r.status} v={`${r.n} · ${Number(r.total).toLocaleString()}`} />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

export default function ViewPromos() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [filters, setFilters] = useState<PromosTableFilters>({ search: "", status: "" });

  useEffect(() => setPage(1), [filters.search, filters.status]);

  const query = useMemo(
    () => ({
      page,
      pageSize,
      search: filters.search?.trim() || undefined,
      status: filters.status || undefined,
    }),
    [page, filters.search, filters.status]
  );
  const { data, isLoading } = usePromosList(query);

  const [simulating, setSimulating] = useState<Promo | null>(null);
  const [codesFor, setCodesFor] = useState<Promo | null>(null);
  const [statsFor, setStatsFor] = useState<Promo | null>(null);
  const [confirm, setConfirm] = useState<{ promo: Promo } | null>(null);

  const deleteMut = useDeletePromoMutation();
  const statusMut = useSetPromoStatusMutation();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AdminLayout>
      <Breadcrumbs />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Promo Codes</h1>
          <p className="text-sm text-gray-500">Create and manage discount campaigns.</p>
        </div>
        <button
          onClick={() => navigate("/promos/new")}
          className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
        >
          <FiPlus /> New Promo
        </button>
      </div>

      {isLoading ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <PlaneLoadingScreen fullscreen={false} label="Loading promos…" />
        </div>
      ) : (
        <PromosTable
          rows={items}
          filters={filters}
          setFilters={setFilters}
          onEdit={(p) => navigate(`/promos/edit/${p.id}`)}
          onDelete={(p) => setConfirm({ promo: p })}
          onGenerateCodes={(p) => setCodesFor(p)}
          onSimulate={(p) => setSimulating(p)}
          onAnalytics={(p) => setStatsFor(p)}
          onToggleStatus={(p, next) => statusMut.mutate({ id: p.id, status: next })}
        />
      )}

      {/* Pagination — same card foot as the other list screens */}
      {!isLoading && (
        <TablePagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {simulating ? <SimulatorModal promo={simulating} onClose={() => setSimulating(null)} /> : null}
      {codesFor ? <CodeBatchModal promo={codesFor} onClose={() => setCodesFor(null)} /> : null}
      {statsFor ? <AnalyticsModal promo={statsFor} onClose={() => setStatsFor(null)} /> : null}

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Delete promo"
        message={confirm ? `Delete "${confirm.promo.name}"? It will be archived and can no longer be redeemed.` : ""}
        confirmText="Delete"
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          deleteMut.mutate(confirm.promo.id, { onSettled: () => setConfirm(null) });
        }}
      />
    </AdminLayout>
  );
}
